import React, { useRef, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, Loader2 } from "lucide-react";


// Uploads a file to object storage and returns the full public URL via onChange.
// `aspect` (e.g. "3/4" or "16/9") renders a live crop preview matching how the image
// will actually appear, so analysts see the exact framing before saving.
export function FileUpload({
  value, onChange, accept = "image/*", label = "Upload", testid = "file-upload",
  aspect = null, previewClass = "w-40", objectPosition = "center 22%",
}) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = () => ref.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const isImg = value && accept.includes("image");

  return (
    <div>
      <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" data-testid={`${testid}-input`} />
      {value ? (
        <div className="space-y-2">
          {isImg && aspect ? (
            <div>
              <div className={`relative ${previewClass} rounded-xl overflow-hidden border border-white/10 bg-black`} style={{ aspectRatio: aspect }}>
                <img src={value} alt="" style={{ objectPosition }} className="absolute inset-0 w-full h-full object-cover" data-testid={`${testid}-preview`} />
                <span className="absolute bottom-1 left-1 font-mono-cl text-[9px] tracking-[0.1em] uppercase text-white/80 bg-black/55 rounded px-1 py-0.5">{aspect.replace("/", ":")} preview</span>
              </div>
            </div>
          ) : (
            isImg && <img src={value} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
          )}
          <div className="flex items-center gap-3">
            <button type="button" onClick={pick} disabled={busy} data-testid={`${testid}-replace`} className="px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-300 hover:border-[#ccff00]/40">{busy ? "Uploading…" : "Replace"}</button>
            <button type="button" onClick={() => onChange("")} className="grid place-items-center w-7 h-7 rounded-full text-red-400 hover:bg-red-500/10"><X className="w-4 h-4" /></button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={pick} disabled={busy} data-testid={`${testid}-btn`}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/15 text-sm text-zinc-400 hover:border-[#ccff00]/40 hover:text-white transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} {busy ? "Uploading…" : label}
        </button>
      )}
    </div>
  );
}
